<template>
  <div class="min-h-screen bg-gray-50 py-8">    
    <!-- Locale switcher - click dropdown -->
    <div class="absolute top-4 right-4 z-10">
      <div class="locale-switcher relative" v-click-outside="closeDropdown">
        <!-- Current language trigger -->
        <button 
          @click="toggleDropdown"
          class="current-locale bg-white rounded-lg shadow-sm border border-gray-200 px-3 py-2 cursor-pointer hover:border-gray-300 transition-colors"
        >
          <div class="flex items-center gap-2">
            <img :src="currentLocaleData.flag" :alt="currentLocale" class="w-4 h-3 object-cover rounded-sm">
            <span class="text-xs font-medium text-gray-700 uppercase">{{ currentLocale }}</span>
            <svg :class="['w-3 h-3 text-gray-400 transition-transform', isDropdownOpen ? 'rotate-180' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </div>
        </button>
        
        <!-- Dropdown menu -->
        <div 
          v-show="isDropdownOpen"
          class="dropdown-menu absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[120px] animate-in fade-in duration-200"
        >
          <button
            v-for="locale in availableLocales"
            :key="locale.code"
            @click="switchLocale(locale.code)"
            :class="[
              'w-full flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors',
              currentLocale === locale.code 
                ? 'bg-blue-50 text-blue-700' 
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            ]"
            :title="locale.name"
          >
            <img :src="locale.flag" :alt="locale.code" class="w-4 h-3 object-cover rounded-sm">
            <span>{{ locale.name }}</span>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Generic landing when no namespace present in URL -->
    <div v-if="genericLanding" class="max-w-2xl mx-auto px-6 py-20 text-center space-y-6">
      <div class="flex flex-col items-center gap-4">
        <img src="/images/campingonline_logo.svg" alt="CampingOnline" class="h-12">
        <h1 class="text-2xl font-semibold text-gray-900">{{ t('landingTitle') }}</h1>
        <p class="text-gray-600 leading-relaxed text-sm sm:text-base max-w-prose">
          {{ t('landingDescriptionPrefix') }}
          <span class="font-medium">{{ t('landingDescriptionStrong') }}</span>.
        </p>
        <p class="text-gray-500 text-sm">{{ t('landingLinkRequired') }}</p>
        <div>
          <a href="https://www.campingonline.se" class="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand text-white text-sm font-medium shadow-sm hover:opacity-90 transition">
            {{ t('landingVisitSite') }}
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
          </a>
        </div>
      </div>
    </div>
    <!-- Loading state -->
    <div v-else-if="loading" class="max-w-2xl mx-auto px-4">
      <div class="flex flex-col items-center justify-center py-24 gap-6" aria-live="polite">
        <div class="relative w-14 h-14 flex items-center justify-center" role="status" :aria-label="t('loading')">
          <svg class="animate-spin text-brand" viewBox="0 0 50 50">
            <circle class="opacity-10 stroke-current" cx="25" cy="25" r="20" fill="none" stroke-width="4" />
            <path class="stroke-current" fill="none" stroke-width="4" stroke-linecap="round"
              d="M25 5a20 20 0 1 1-14.14 34.14" />
          </svg>
        </div>
        <div class="flex flex-col items-center gap-1">
          <p class="text-sm text-gray-600 font-medium">{{ t('fetchingInvoice') }}</p>
          <p class="text-xs text-gray-400 tracking-wide">{{ t('pleaseWait') }}</p>
        </div>
      </div>
    </div>
    <!-- Error state -->
    <div v-else-if="loadError" class="max-w-2xl mx-auto px-4" aria-live="polite">
      <div class="border border-red-200 bg-red-50 text-red-700 rounded-lg p-5 space-y-4">
        <div class="flex items-center gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 9v4m0 4h.01M4.93 4.93l14.14 14.14M12 2a10 10 0 100 20 10 10 0 000-20z" />
          </svg>
          <p class="font-medium">{{ t('fetchErrorTitle') }}</p>
        </div>
        <p class="text-sm leading-relaxed">{{ loadError }}</p>
        <div>
          <button @click="fetchInvoice"
            class="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition disabled:opacity-50"
            :disabled="loading">
            {{ t('retry') }}
          </button>
        </div>
      </div>
    </div>
    <!-- Main content -->
    <div v-else class="max-w-2xl mx-auto px-4">
      <!-- Success banner for recent payment (independent of full balance status) -->
      <div v-if="recentPaymentSuccess"
        class="mb-6 border border-green-200 bg-green-50 text-green-700 text-sm rounded-lg px-4 py-3 flex items-start gap-2"
        aria-live="polite">
        <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
        <div>
          <p class="font-medium">{{ t('thanksPayment') }}</p>
          <p class="text-green-600/80" v-if="remainingAmount > 0">{{ t('paymentRegisteredRemaining') }}</p>
          <p class="text-green-600/80" v-else>{{ t('paymentRegisteredPaid') }}</p>
        </div>
      </div>
      <!-- Header with campground logo -->
      <div class="text-center mb-8">
        <!-- Show logo if available -->
        <img v-if="invoice.campground.logo" :src="invoice.campground.logo" :alt="invoice.campground.name"
          class="max-h-30 min-h-12 max-w-xs mx-auto mb-4 object-contain">
          <!-- Fallback: campground name if no logo -->
        <div v-else
          class="h-16 mx-auto mb-4 flex items-center justify-center rounded-sm bg-gray-200 text-gray-700 font-semibold text-xl px-4"
          :aria-label="invoice.campground.name">
          {{ invoice.campground.name }}
        </div>
      </div>

      <!-- Main invoice card -->
      <div class="card p-6 mb-6">
        <!-- Invoice header / top row -->
        <div class="flex flex-col gap-4 mb-6">
          <div class="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 class="text-xl font-semibold text-gray-900 leading-tight">{{ t('invoice') }} {{ invoice.number }}</h2>
            </div>
            <div class="flex items-center gap-3">
              <button @click="downloadInvoice"
                class="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-xs transition">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {{ t('downloadPdf') }}
              </button>
              <span :class="['inline-flex items-center px-3 py-1 rounded-full text-sm font-medium', getStatusClass()]">
                {{ getStatusText() }}
              </span>
            </div>
          </div>

          <!-- Meta / customer grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4 border-t border-gray-100">
            <!-- Customer (left) -->
            <div>
              <h3 class="text-xs font-semibold tracking-wide text-gray-500 uppercase mb-3">{{ t('customerDetails') }}</h3>
              <div class="text-sm text-gray-700 space-y-0.5">
                <p class="font-medium text-gray-900">{{ invoice.customer.name }}</p>
                <p v-if="invoice.customer.street">{{ invoice.customer.street }}</p>
                <p v-if="invoice.customer.line2">{{ invoice.customer.line2 }}</p>
                <p v-if="invoice.customer.zip || invoice.customer.city">{{ invoice.customer.zip }}<span
                    v-if="invoice.customer.zip && invoice.customer.city"> </span> {{ invoice.customer.city }}</p>
              </div>
            </div>
            <!-- Invoice meta (right) -->
            <div>
              <h3 class="text-xs font-semibold tracking-wide text-gray-500 uppercase mb-3">{{ t('invoiceSection') }}</h3>
              <dl class="text-sm text-gray-700 space-y-1.5">
                <div class="flex justify-between">
                  <dt class="text-gray-500">{{ t('invoiceDate') }}</dt>
                  <dd class="text-gray-900">{{ formatDate(invoice.issueDate) }}</dd>
                </div>
                <div class="flex justify-between">
                  <dt class="text-gray-500">{{ t('payBy') }}</dt>
                  <dd class="text-gray-900">{{ formatDate(invoice.dueDate) }}</dd>
                </div>
                <div class="flex justify-between">
                  <dt class="text-gray-500">{{ t('interest') }}</dt>
                  <dd class="text-gray-900">{{ t('referenceRatePlus', { rate: invoice.interestRate }) }}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        <!-- Invoice items -->
        <div class="mb-6 bg-gray-50 rounded-lg p-4">
          <h3 class="text-sm font-medium text-gray-900 mb-3">{{ t('specification') }}</h3>
          <table class="invoice-table">
            <tbody>
              <tr v-for="item in invoice.items" :key="item.id" class="border-b border-gray-100 last:border-0">
                <td class="py-3">
                  <div class="font-medium text-gray-900">{{ item.description }}</div>
                  <div v-if="item.period" class="text-sm text-gray-500">{{ item.period }}</div>
                </td>
                <td class="amount text-gray-900">{{ formatCurrency(item.amount) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Totals -->
        <div class="border-t border-gray-200 pt-4">
          <div class="space-y-2">
            <div class="flex justify-between text-sm">
              <span class="text-gray-600">{{ t('netPrice') }}</span>
              <span class="text-gray-900">{{ formatCurrency(invoice.subtotal) }}</span>
            </div>
            <div v-for="row in vatRows" :key="row.title" class="flex justify-between text-sm">
              <span class="text-gray-600">{{ t('vat') }} ({{ row.title }})</span>
              <span class="text-gray-900">{{ formatCurrency(oreToSEK(row.vat)) }}</span>
            </div>
            <div v-if="invoice.rounding !== 0" class="flex justify-between text-sm">
              <span class="text-gray-600">{{ t('rounding') }}</span>
              <span class="text-gray-900">{{ formatCurrency(invoice.rounding) }}</span>
            </div>
            <div class="flex justify-between text-lg font-semibold pt-2 border-t border-gray-100">
              <span class="text-gray-900">{{ t('toPay') }}</span>
              <span class="text-brand">{{ formatCurrency(invoice.total) }}</span>
            </div>
            <!-- Paid / Remaining (conditional) -->
            <div v-if="invoice.paidAmount > 0 || invoice.creditedAmount > 0"
              class="mt-2 pt-2 border-t border-dashed border-gray-200 space-y-2 text-sm">
              <div v-if="invoice.paidAmount > 0" class="flex justify-between">
                <span class="text-gray-500">{{ t('paid') }}</span>
                <span class="text-gray-700">{{ formatCurrency(invoice.paidAmount) }}</span>
              </div>
              <div v-if="invoice.creditedAmount > 0" class="flex justify-between">
                <span class="text-gray-500">{{ t('credited') }}</span>
                <span class="text-gray-700">{{ formatCurrency(invoice.creditedAmount) }}</span>
              </div>
              <div class="flex justify-between font-medium"
                :class="remainingAmount === 0 ? 'text-green-600' : 'text-gray-900'">
                <span>{{ t('remaining') }}</span>
                <span>{{ formatCurrency(remainingAmount) }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- Contact info -->
        <div class="mt-6 p-4 bg-gray-50 rounded-lg">
          <p class="text-sm text-gray-600">
            {{ t('contactQuestions') }}
            <a :href="`mailto:${invoice.campground.email}`" class="text-brand hover:underline">{{
              invoice.campground.email }}</a>
            <span class="mx-1">·</span>
            <a :href="phoneHref" class="text-brand hover:underline">{{ invoice.campground.phone }}</a>
          </p>
        </div>
      </div>

      <!-- Success banner if fully paid -->
      <div v-if="remainingAmount === 0"
        class="mb-6 border border-green-200 bg-green-50 text-green-700 text-sm rounded-lg px-4 py-3 flex items-start gap-2">
        <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
        <div>
          <p class="font-medium">{{ t('invoicePaidBannerTitle') }}</p>
          <p class="text-green-600/80">{{ t('invoicePaidBannerSubtitle') }}</p>
        </div>
      </div>

      <!-- Payment section (only if remaining) -->
      <div v-if="remainingAmount > 0" ref="paymentSectionRef" class="card p-6 mb-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-5">{{ t('payment') }}</h3>

        <!-- Payment methods (other methods + Klarna callout) -->
        <div class="mb-8">
          <div class="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
            <!-- Standard payment method logos (excluding Klarna) -->
            <div>
              <p class="text-sm font-medium text-gray-900 mb-3">{{ t('weAccept') }}</p>
              <div class="grid grid-cols-3 sm:grid-cols-5 gap-4">
                <div v-for="method in displayPaymentLogos" :key="method"
                  class="flex items-center justify-center p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
                  <img :src="getPaymentMethodLogo(method)" :alt="method" class="h-8" loading="lazy">
                </div>
              </div>
            </div>
            <!-- Klarna BNPL explanatory callout (single appearance) -->
            <div v-if="paymentMethods.includes('klarna')"
              class="flex items-center gap-4 border border-pink-200/60 bg-pink-50 rounded-md p-4">
              <img src="/images/klarna.png" alt="Klarna" class="h-8"
                loading="lazy">
              <p class="text-xs sm:text-sm text-pink-900 leading-snug">{{ t('klarnaText') }}</p>
            </div>
            <div class="mb-2 text-right">
              <div class="inline-flex items-center gap-2 text-[11px] sm:text-xs text-gray-500">
                <span>{{ t('securePaymentsProvidedBy') }}</span>
                <img src="/images/surfboard.svg" alt="Surfboard Payments"
                  class="h-5 w-auto opacity-90" />
              </div>
            </div>
          </div>
        </div>

        
        <!-- Custom amount trigger link -->
        <div v-if="showCustomAmountOption && !useCustomAmount" class="mb-6">
          <button type="button" @click="activateCustomAmount"
            class="text-xs text-brand hover:underline focus:outline-hidden focus:ring-3 focus:ring-blue-300 rounded-xs flex items-center gap-1">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            {{ t('manualPartPayment') }}
          </button>
        </div>
        <!-- Custom amount input (active) -->
        <div v-if="showCustomAmountOption && useCustomAmount" class="mb-6 space-y-2">
          <div class="flex items-center gap-3">
            <div class="relative">
              <input
                v-model="customAmountInput"
                ref="customAmountInputRef"
                @input="sanitizeCustomAmount"
                :class="['w-40 px-3 py-2 rounded-md border text-sm focus:outline-hidden focus:ring-3 focus:ring-brand/40 transition', customAmountError ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white']"
                :placeholder="t('enterAmount')"
                inputmode="decimal"
                aria-describedby="customAmountHelp"
              />
              <span class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">{{ t('currencySek') }}</span>
            </div>
            <button type="button" @click="cancelCustomAmount" class="text-[11px] text-gray-500 hover:underline">{{ t('cancel') }}</button>
          </div>
          <p id="customAmountHelp" class="text-xs text-gray-500">{{ t('minMaxHelp', { min: MIN_PART_PAYMENT, max: formatCurrency(remainingAmount), days: daysLeft }) }}</p>
          <p v-if="customAmountError" class="text-xs text-red-600 flex items-center gap-1">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M12 5a7 7 0 100 14 7 7 0 000-14z" /></svg>
            {{ customAmountError }}
          </p>
        </div>

        <!-- Action buttons -->
        <div class="flex flex-col gap-3">
          <div v-if="paymentError" class="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 mb-2"
            aria-live="polite">
            <div class="font-semibold mb-1 flex items-center gap-1">
              <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 9v4m0 4h.01M4.93 4.93l14.14 14.14M12 2a10 10 0 100 20 10 10 0 000-20z" />
              </svg>
              {{ t('paymentProblem') }}
            </div>
            <div class="text-sm">{{ paymentError }}</div>
          </div>
          <button @click="processPayment" :disabled="processing || (useCustomAmount && !!customAmountError)"
            class="btn-primary w-full flex items-center justify-center gap-2 text-lg py-4 disabled:opacity-60 disabled:cursor-not-allowed">
            <div v-if="processing" class="relative w-5 h-5" :aria-label="t('initiatingPayment')" role="status">
              <svg class="animate-spin text-white" viewBox="0 0 50 50">
                <circle class="opacity-30 stroke-current" cx="25" cy="25" r="20" fill="none" stroke-width="5" />
                <circle class="stroke-current" cx="25" cy="25" r="20" fill="none" stroke-width="5"
                  stroke-linecap="round" stroke-dasharray="110" stroke-dashoffset="80">
                  <animate attributeName="stroke-dashoffset" values="80;0" dur="1s" repeatCount="indefinite" />
                </circle>
              </svg>
            </div>
            <span>{{ processing ? t('pleaseWaitAction') : (useCustomAmount && customAmountNumber ? t('payAmount', { amount: formatCurrency(customAmountNumber) }) : t('goToPayment')) }}</span>
          </button>
        </div>

        <!-- Bankgiro disclaimer -->
        <p v-if="invoice.campground.bankgiro" class="text-xs text-gray-500 mt-4 text-center">
          {{ t('bankgiroDisclaimer') }}
        </p>
      </div>

      <!-- Company details footer -->
      <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <div class="flex justify-between items-start text-sm text-gray-600">
          <div>
            <p class="font-medium text-gray-900">{{ invoice.campground.name }}</p>
            <p>{{ invoice.campground.address }}</p>
            <p><a :href="`mailto:${invoice.campground.email}`" class="hover:underline text-gray-700">{{
              invoice.campground.email }}</a></p>
            <p><a :href="phoneHref" class="hover:underline text-gray-700">{{ invoice.campground.phone }}</a></p>
          </div>
          <div class="text-right space-y-0.5">
            <p>{{ t('orgNr') }} {{ invoice.campground.orgNumber }}</p>
            <p>{{ t('vatNr') }} {{ invoice.campground.vatNumber }}</p>
            <p v-if="invoice.campground.bankgiro" class="text-gray-500">{{ t('bankgiro') }}: {{ invoice.campground.bankgiro }}</p>
            <p v-if="invoice.ocrNumber" class="text-gray-500">{{ t('ocr') }}: {{ invoice.ocrNumber }}</p>
          </div>
        </div>
      </div>

      <!-- CampingOnline branding footer -->
      <div class="text-center">
        <div class="text-sm text-gray-500 mb-2">
          {{ t('paymentLinksBranding') }}
        </div>
        <div class="flex items-center justify-center">
          <img src="/images/campingonline_logo.svg" alt="CampingOnline" class="h-8">
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, nextTick, watch } from 'vue'
import axios from 'axios'
import { createI18n } from 'vue-i18n'

// Click outside directive
const vClickOutside = {
  beforeMount(el: any, binding: any) {
    el.clickOutsideEvent = (event: Event) => {
      if (!(el === event.target || el.contains(event.target as Node))) {
        binding.value()
      }
    }
    document.addEventListener('click', el.clickOutsideEvent)
  },
  unmounted(el: any) {
    document.removeEventListener('click', el.clickOutsideEvent)
  }
}

const messages = {
  sv: {
    loading: 'Laddar',
    landingTitle: 'CampingOnline Betallänkar',
    landingDescriptionPrefix: 'Detta är en publik endpoint för CampingOnline Betallänkar – en del av',
    landingDescriptionStrong: 'Sveriges snabbast växande bokningssystem helt inriktat på camping',
    landingLinkRequired: 'För att visa en faktura krävs en komplett länk med ett campingnamn / faktura-id / kod.',
    landingVisitSite: 'Besök campingonline.se',
    fetchingInvoice: 'Hämtar faktura',
    pleaseWait: 'Vänligen vänta...',
    fetchErrorTitle: 'Fel vid hämtning',
    retry: 'Försök igen',
    thanksPayment: 'Tack för din betalning',
    paymentRegisteredRemaining: 'Din betalning har registrerats. Kvarstående belopp visas nedan.',
    paymentRegisteredPaid: 'Din betalning har registrerats och fakturan är nu helt betald.',
    invoice: 'Faktura',
    invoiceDate: 'Fakturadatum',
    payBy: 'Betala senast',
    interest: 'Dröjsmålsränta',
    referenceRatePlus: 'Referensränta + {rate}%',
    customerDetails: 'Kunduppgifter',
    invoiceSection: 'Faktura',
    specification: 'Specifikation',
    netPrice: 'Pris (exkl. moms)',
    rounding: 'Öresavrundning',
    toPay: 'Att betala',
    paid: 'Betalt',
    credited: 'Krediterat',
    remaining: 'Kvar att betala',
    invoicePaidBannerTitle: 'Fakturan är betald',
    invoicePaidBannerSubtitle: 'Tack! Ingen ytterligare åtgärd krävs.',
    payment: 'Betalning',
    weAccept: 'Vi accepterar',
    klarnaText: 'Betala nu, senare eller dela upp din betalning med Klarna.',
    securePaymentsProvidedBy: 'Säkra betalningar tillhandahålls av',
    manualPartPayment: 'Manuell delbetalning',
    minMaxHelp: 'Minst {min} SEK · Max {max} · Förfaller om {days} dagar.',
    enterAmount: 'Ange ett belopp',
    currencySek: 'SEK',
    minPartPayment: 'Minsta delbetalning är {min} SEK',
    amountExceeds: 'Beloppet får inte överstiga kvarstående',
    cancel: 'Avbryt',
    paymentProblem: 'Problem med betalning',
    initiatingPayment: 'Initierar betalning',
    pleaseWaitAction: 'Vänligen vänta...',
    payAmount: 'Betala {amount}',
    goToPayment: 'Till betalning',
    bankgiroDisclaimer: 'Om du har betalat genom bankgiro eller banköverföring kan det ta några arbetsdagar innan det registreras. Kontakta campingen om kvarstående belopp inte stämmer.',
    orgNr: 'Org.nr',
    vatNr: 'Momsreg.nr',
    bankgiro: 'Bankgiro',
    ocr: 'OCR',
    paymentLinksBranding: 'Betallänkar är en säker tjänst från',
    overdue: 'Försenad',
    paidStatus: 'Betald',
    daysLeft_one: '{count} dag kvar',
    daysLeft_other: '{count} dagar kvar',
    partial: 'Delbetald',
    unpaid: 'Obetald',
    vat: 'Moms',
    contactQuestions: 'För frågor om fakturans innehåll kontakta campingen på',
    downloadPdf: 'Hämta PDF',
    paymentFailedError: 'Betalningsförsöket kunde inte slutföras. Vänligen prova en alternativ betalmetod.',
    paymentPendingError: 'Vi har ännu inte kunnat bekräfta betalningen. Du kan försöka igen – om båda betalningarna slutförs registreras det extra beloppet på ditt kundsaldo.',
    demoInvoiceAlert: 'Exempelfaktura, endast för demonstration. Ingen betalning.',
    demoPdfAlert: 'Exempelfaktura, ingen PDF att hämta.',
    genericInvoiceError: 'Fakturan kunde inte hämtas, vänligen försök igen senare',
    initiatePaymentError: 'Kunde inte initiera betalningen, vänligen försök igen senare'
  },
  en: {
    loading: 'Loading',
    landingTitle: 'CampingOnline Payment Links',
    landingDescriptionPrefix: 'This is a public endpoint for CampingOnline Payment Links – part of',
    landingDescriptionStrong: "Sweden's fastest growing booking system dedicated to camping",
    landingLinkRequired: 'A complete link with campsite name / invoice ID / code is required to view an invoice.',
    landingVisitSite: 'Visit campingonline.se',
    fetchingInvoice: 'Fetching invoice',
    pleaseWait: 'Please wait...',
    fetchErrorTitle: 'Fetch error',
    retry: 'Try again',
    thanksPayment: 'Thank you for your payment',
    paymentRegisteredRemaining: 'Your payment has been registered. Remaining amount is shown below.',
    paymentRegisteredPaid: 'Your payment has been registered and the invoice is now fully paid.',
    invoice: 'Invoice',
    invoiceDate: 'Invoice date',
    payBy: 'Pay by',
    interest: 'Default interest',
    referenceRatePlus: 'Reference rate + {rate}%',
    customerDetails: 'Customer details',
    invoiceSection: 'Invoice',
    specification: 'Specification',
    netPrice: 'Price (excl. VAT)',
    rounding: 'Rounding',
    toPay: 'Total',
    paid: 'Paid',
    credited: 'Credited',
    remaining: 'Remaining',
    invoicePaidBannerTitle: 'Invoice paid',
    invoicePaidBannerSubtitle: 'Thank you! No further action required.',
    payment: 'Payment',
    weAccept: 'We accept',
    klarnaText: 'Pay now, pay later or pay in installments with Klarna.',
    securePaymentsProvidedBy: 'Secure payments provided by',
    manualPartPayment: 'Manual part payment',
    minMaxHelp: 'Minimum {min} SEK · Max {max} · Due in {days} days.',
    enterAmount: 'Enter an amount',
    currencySek: 'SEK',
    minPartPayment: 'Minimum part payment is {min} SEK',
    amountExceeds: 'Amount may not exceed remaining',
    cancel: 'Cancel',
    paymentProblem: 'Payment problem',
    initiatingPayment: 'Initiating payment',
    pleaseWaitAction: 'Please wait...',
    payAmount: 'Pay {amount}',
    goToPayment: 'Proceed to payment',
    bankgiroDisclaimer: 'If you paid via bankgiro / bank transfer it may take a few business days to register. Contact the campsite if the remaining balance does not match.',
    orgNr: 'Org. no.',
    vatNr: 'VAT no.',
    bankgiro: 'Bankgiro',
    ocr: 'OCR',
    paymentLinksBranding: 'Payment Links is a secure service by',
    overdue: 'Overdue',
    paidStatus: 'Paid',
    daysLeft_one: '{count} day left',
    daysLeft_other: '{count} days left',
    partial: 'Partially paid',
    unpaid: 'Unpaid',
    vat: 'VAT',
    contactQuestions: 'For questions about the invoice contact the campsite at',
    downloadPdf: 'Download PDF',
    paymentFailedError: 'The payment attempt could not be completed. Please try an alternative payment method.',
    paymentPendingError: 'We have not yet been able to confirm the payment. You can try again – if both payments complete, the extra amount will be registered on your customer balance.',
    demoInvoiceAlert: 'Demo invoice, for demonstration only. No payment.',
    demoPdfAlert: 'Demo invoice, no PDF available.',
    genericInvoiceError: 'Could not fetch invoice, please try again later',
    initiatePaymentError: 'Could not initiate payment, please try again later'
  },
  de: {
    loading: 'Laden',
    landingTitle: 'CampingOnline Zahlungslinks',
    landingDescriptionPrefix: 'Dies ist ein öffentlicher Endpunkt für CampingOnline Zahlungslinks – Teil von',
    landingDescriptionStrong: 'Schwedens am schnellsten wachsendem Buchungssystem speziell für Camping',
    landingLinkRequired: 'Zum Anzeigen einer Rechnung wird ein vollständiger Link mit Campingplatzname / Rechnungs-ID / Code benötigt.',
    landingVisitSite: 'campingonline.se besuchen',
    fetchingInvoice: 'Rechnung wird geladen',
    pleaseWait: 'Bitte warten...',
    fetchErrorTitle: 'Fehler beim Laden',
    retry: 'Erneut versuchen',
    thanksPayment: 'Vielen Dank für Ihre Zahlung',
    paymentRegisteredRemaining: 'Ihre Zahlung wurde registriert. Der Restbetrag wird unten angezeigt.',
    paymentRegisteredPaid: 'Ihre Zahlung wurde registriert und die Rechnung ist vollständig bezahlt.',
    invoice: 'Rechnung',
    invoiceDate: 'Rechnungsdatum',
    payBy: 'Fällig am',
    interest: 'Verzugszins',
    // Viktigt att benämna svensk här så att det inte förväxlas med tysk
    referenceRatePlus: 'Schwedischer Leitzins + {rate}%',
    customerDetails: 'Kundendaten',
    invoiceSection: 'Rechnung',
    specification: 'Spezifikation',
    netPrice: 'Preis (exkl. MwSt.)',
    rounding: 'Rundung',
    toPay: 'Gesamt',
    paid: 'Bezahlt',
    credited: 'Gutgeschrieben',
    remaining: 'Restbetrag',
    invoicePaidBannerTitle: 'Rechnung bezahlt',
    invoicePaidBannerSubtitle: 'Danke! Keine weitere Aktion erforderlich.',
    payment: 'Zahlung',
    weAccept: 'Wir akzeptieren',
    klarnaText: 'Jetzt, später oder in Raten mit Klarna bezahlen.',
    securePaymentsProvidedBy: 'Sichere Zahlungen bereitgestellt von',
    manualPartPayment: 'Teilzahlung manuell',
    minMaxHelp: 'Mindestens {min} SEK · Max {max} · Fällig in {days} Tagen.',
    enterAmount: 'Betrag eingeben',
    currencySek: 'SEK',
    minPartPayment: 'Die minimale Teilzahlung beträgt {min} SEK',
    amountExceeds: 'Der Betrag darf den Restbetrag nicht überschreiten',
    cancel: 'Abbrechen',
    paymentProblem: 'Zahlungsproblem',
    initiatingPayment: 'Zahlung wird gestartet',
    pleaseWaitAction: 'Bitte warten...',
    payAmount: 'Jetzt {amount} zahlen',
    goToPayment: 'Zur Zahlung',
    bankgiroDisclaimer: 'Bei Bankgiro / Überweisung kann die Verbuchung einige Tage dauern. Kontaktieren Sie den Campingplatz bei Unstimmigkeiten.',
    orgNr: 'Org.-Nr.',
    vatNr: 'USt-IdNr.',
    bankgiro: 'Bankgiro',
    ocr: 'OCR',
    paymentLinksBranding: 'Zahlungslinks ist ein sicherer Dienst von',
    overdue: 'Überfällig',
    paidStatus: 'Bezahlt',
    daysLeft_one: '{count} Tag übrig',
    daysLeft_other: '{count} Tage übrig',
    partial: 'Teilweise bezahlt',
    unpaid: 'Unbezahlt',
    vat: 'MwSt.',
    contactQuestions: 'Bei Fragen zum Inhalt der Rechnung kontaktieren Sie den Campingplatz unter',
    downloadPdf: 'PDF herunterladen',
    paymentFailedError: 'Der Zahlungsversuch konnte nicht abgeschlossen werden. Bitte versuchen Sie eine alternative Zahlungsmethode.',
    paymentPendingError: 'Wir konnten die Zahlung noch nicht bestätigen. Sie können es erneut versuchen – wenn beide Zahlungen abgeschlossen werden, wird der zusätzliche Betrag Ihrem Kundenguthaben gutgeschrieben.',
    demoInvoiceAlert: 'Beispielrechnung, nur zur Demonstration. Keine Zahlung.',
    demoPdfAlert: 'Beispielrechnung, kein PDF verfügbar.',
    genericInvoiceError: 'Rechnung konnte nicht abgerufen werden, bitte versuchen Sie es später erneut',
    initiatePaymentError: 'Zahlung konnte nicht gestartet werden, bitte versuchen Sie es später erneut'
  }
}

const urlLang = new URLSearchParams(window.location.search).get('lang')
const initialLocale = (urlLang === 'en' || urlLang === 'de') ? urlLang : 'sv'
const i18n = createI18n({ legacy: false, locale: initialLocale, fallbackLocale: 'sv', messages, pluralRules: {
  sv: (choice) => choice === 1 ? 1 : 2,
  en: (choice) => choice === 1 ? 1 : 2,
  de: (choice) => choice === 1 ? 1 : 2
}})

// expose t
const { t, locale } = i18n.global

// Locale switcher data
const availableLocales = ref([
  { code: 'sv', name: 'Svenska', flag: '/images/flag-sv.svg' },
  { code: 'en', name: 'English', flag: '/images/flag-en.svg' },
  { code: 'de', name: 'Deutsch', flag: '/images/flag-de.svg' }
])

const currentLocale = computed(() => locale.value)

const currentLocaleData = computed(() => 
  availableLocales.value.find(l => l.code === currentLocale.value) || availableLocales.value[0]
)

// Dropdown state and methods
const isDropdownOpen = ref(false)

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value
}

const closeDropdown = () => {
  isDropdownOpen.value = false
}

const switchLocale = (newLocale: string) => {
  if (newLocale === 'sv' || newLocale === 'en' || newLocale === 'de') {
    locale.value = newLocale as 'sv' | 'en' | 'de'
    // Update URL with new language parameter
    const url = new URL(window.location.href)
    url.searchParams.set('lang', newLocale)
    window.history.replaceState({}, '', url.toString())
    // Close dropdown after selection
    closeDropdown()
  }
}

// --- Routing / path parsing ---
// Expected path: /:namespace/:invoice_id/:code
let routeNamespace = ''
let routeInvoiceId = ''
let routeCode = ''
const segments = window.location.pathname.replace(/^\/+/, '').split('/')
// segments[0]=namespace, [1]=invoice_id, [2]=code
routeNamespace = segments[0] || ''
routeInvoiceId = segments[1] || ''
routeCode = segments[2] || ''

// Detect status param early (success/error redirect from payment)
const statusParam = new URLSearchParams(window.location.search).get('status')
const paymentAttemptIdParam = new URLSearchParams(window.location.search).get('payment_attempt_id')

// --- State ---
const loading = ref(true)
const loadError = ref<string | null>(null)
const processing = ref(false)
const paymentError = ref<string | null>(null)
const recentPaymentSuccess = ref(false)
const returnedPaymentAttemptStatus = ref<string | null>(null)
const paymentSectionRef = ref<HTMLElement | null>(null)
const genericLanding = ref(false)

// Raw API dto
interface VatRow { title: string; sale: number; vat: number }
interface SalesItem { header?: string | null; description?: string | null; sales_amount: number }
interface InvoiceDto {
  invoice_id: string | number
  ocr: string
  status: 'open' | 'partial' | 'closed'
  signed_pdf_link?: string | null
  org_name?: string | null
  full_name: string | null
  address_line_1?: string | null
  address_line_2?: string | null
  invoice_date: string // ISO
  due_date: string // ISO
  total_amount: number
  paid_amount: number
  credited_amount: number
  rounding?: number | null
  vat_table: VatRow[]
  sales_items: SalesItem[]
  surfboard_payment_methods: string[] // e.g. ['CARD','SWISH','KLARNA']
  camping_name: string
  camping_address: string
  camping_email: string
  camping_phone: string
  camping_org_number: string
  camping_vat_number: string
  camping_bankgiro: string
  camping_logo?: string | null
}
interface InvoiceApiSuccessResponse { status: 'success'; data: { invoice: InvoiceDto; payment_attempt_status?: string | null } }
interface InvoiceApiErrorResponse { status: 'error'; error_message?: string }
type InvoiceApiResponse = InvoiceApiSuccessResponse | InvoiceApiErrorResponse

// Internal normalized invoice model used by template
const invoice = reactive({
  number: '',
  issueDate: new Date(),
  dueDate: new Date(),
  interestRate: 8, // Referensränta + 8% (hardcoded adjustment if needed)
  campground: {
    name: '',
    address: '',
    email: '',
    logo: '',
    phone: '',
    vatNumber: '',
    orgNumber: '',
    bankgiro: ''
  },
  customer: {
    name: '',
    street: '',
    line2: '',
    zip: '',
    city: ''
  },
  items: [] as { id: number; description: string; period?: string; amount: number }[],
  subtotal: 0,
  vatAmount: 0,
  total: 0,
  paidAmount: 0,
  creditedAmount: 0,
  ocrNumber: '',
  signedPdfLink: '',
  currency: 'SEK',
  status: 'pending',
  rounding: 0
})

// Additional derived collections
const vatRows = ref<VatRow[]>([])
const paymentMethods = ref<string[]>([])

// Keep raw invoice id for payment initiation
let rawInvoiceId: string | number = ''

// Helpers
const oreToSEK = (v: number | null | undefined) => (v ?? 0) / 100

const GENERIC_INVOICE_ERROR = t('genericInvoiceError')

// Dynamic API base depending on environment & namespace
const getApiBase = () => {
  const host = window.location.hostname
  const isLocal = /localhost|127\.|\.test$/i.test(host) || routeNamespace === 'local'
  if (isLocal) return 'https://server.test'
  if (routeNamespace) return `https://${routeNamespace}.campingonline.se`
  return 'https://server.test'
}

// Fetch logic
const fetchInvoice = async () => {
  if (!routeNamespace) {
    genericLanding.value = true
    loading.value = false
    return
  }
  if (!routeInvoiceId || !routeCode) {
    loadError.value = 'Ogiltig länk'
    loading.value = false
    return
  }
    if (routeNamespace === 'exempel') {   
    Object.assign(invoice, {
      number: '#123456',
      issueDate: new Date('2025-08-01'),
      dueDate: new Date('2026-09-01'),
      interestRate: 8,
      campground: {
        name: 'Demo Camping',
        address: 'Campingvägen 1, 123 45 Stockholm',
        email: 'info@democamping.se',
        logo: '/images/demo.png',
        phone: '070-1234567',
        vatNumber: 'SE1234567890',
        orgNumber: '556677-8899',
        bankgiro: '123-4567'
      },
      customer: {
        name: 'Anders Svensson',
        street: 'Tältgatan 2',
        line2: '',
        zip: '12345',
        city: 'Campinghamn'
      },
      items: [
        { id: 1, description: 'Campingtomt Deluxe', period: '25 aug till 29 aug', amount: 2000 },
        { id: 2, description: 'Frukostpaket', period: '', amount: 200 }
      ],
      subtotal: 1964,
      vatAmount: 236,
      total: 2200,
      paidAmount: 0,
      ocrNumber: '1234567890',
      signedPdfLink: '',
      currency: 'SEK',
      status: 'pending',
      rounding: 0
    })
    vatRows.value = [
      { title: '12%', sale: 220000, vat: 23600 }
    ]
    paymentMethods.value = ['card', 'swish','klarna']
    loading.value = false
    return
  }
  try {
    loading.value = true
    loadError.value = null
    const url = `${getApiBase()}/payments/invoice`
    const params: Record<string, any> = { invoice_id: routeInvoiceId, code: routeCode }
    if (paymentAttemptIdParam) params.payment_attempt_id = paymentAttemptIdParam
    const resp = await axios.get<InvoiceApiResponse>(url, {
      params,
      headers: { Accept: 'application/json' }
    })
    if (resp.data.status === 'error') {
      throw new Error(resp.data.error_message || GENERIC_INVOICE_ERROR)
    }
    const invoiceData = resp.data.data?.invoice
    if (!invoiceData) throw new Error(GENERIC_INVOICE_ERROR)
    returnedPaymentAttemptStatus.value = resp.data.data?.payment_attempt_status ?? null

    rawInvoiceId = invoiceData.invoice_id
    // Map data -> invoice
    invoice.number = '#' + String(invoiceData.invoice_id)
    invoice.issueDate = new Date(invoiceData.invoice_date)
    invoice.dueDate = new Date(invoiceData.due_date)
    invoice.campground.name = invoiceData.camping_name
    invoice.campground.address = invoiceData.camping_address
    invoice.campground.email = invoiceData.camping_email
    invoice.campground.logo = invoiceData.camping_logo || ''
    invoice.campground.phone = invoiceData.camping_phone
    invoice.campground.vatNumber = invoiceData.camping_vat_number
    invoice.campground.orgNumber = invoiceData.camping_org_number
    invoice.campground.bankgiro = invoiceData.camping_bankgiro || ''

    invoice.signedPdfLink = invoiceData.signed_pdf_link || ''

    // Customer name preference
    invoice.customer.name = invoiceData.full_name || invoiceData.org_name || 'Kund'
    const line1 = invoiceData.address_line_1 || ''
    const line2 = invoiceData.address_line_2 || ''
    // Extract zip + city if present inside line1 (assumes format '123 45 City' or '12345 City')
    const zipCityMatch = line1.match(/(\d{3}\s?\d{2})\s+(.+)/)
    if (zipCityMatch) {
      invoice.customer.zip = zipCityMatch[1]
      invoice.customer.city = zipCityMatch[2]
      // Attempt to split potential street before zip (not provided in current pattern). If entire line is zip+city, leave street empty.
      invoice.customer.street = ''
    } else {
      invoice.customer.street = line1
    }
    invoice.customer.line2 = line2

    invoice.ocrNumber = invoiceData.ocr || ''
    invoice.total = oreToSEK(invoiceData.total_amount) // gross
    invoice.paidAmount = oreToSEK(invoiceData.paid_amount)
    invoice.creditedAmount = oreToSEK(invoiceData.credited_amount)

    invoice.items = invoiceData.sales_items.map((si, idx) => ({
      id: idx + 1,
      description: si.header || '',
      period: si.description || undefined,
      amount: oreToSEK(si.sales_amount)
    }))

    vatRows.value = invoiceData.vat_table.map(r => ({ ...r }))
    const vatSum = invoiceData.vat_table.reduce((s, r) => s + r.vat, 0)
    // Each vat_table.sale is gross (includes vat); net per row = sale - vat
    const netSum = invoiceData.vat_table.reduce((s, r) => s + (r.sale - r.vat), 0)
    invoice.subtotal = oreToSEK(netSum)
    invoice.vatAmount = oreToSEK(vatSum)   

    invoice.status = invoiceData.status === 'closed' ? 'paid' : (invoiceData.status === 'partial' ? 'partial' : 'pending')

    paymentMethods.value = (invoiceData.surfboard_payment_methods || []).map(m => m.toLowerCase())

    invoice.rounding = oreToSEK(invoiceData.rounding ?? 0)
  } catch (e: any) {
    loadError.value = e?.message || GENERIC_INVOICE_ERROR
  } finally {
    loading.value = false
  }
}

const scrollToPayment = () => {
  if (paymentSectionRef.value) {
    paymentSectionRef.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

const verifyReturnedPayment = async () => {
  const maxPolls = 12
  for (let poll = 0; poll < maxPolls; poll++) {
    await fetchInvoice()
    if (returnedPaymentAttemptStatus.value === 'registered') {
      recentPaymentSuccess.value = true
      return
    }
    if (returnedPaymentAttemptStatus.value === 'cancelled') {
      paymentError.value = t('paymentFailedError')
      return
    }
    if (poll < maxPolls - 1) {
      await new Promise(resolve => setTimeout(resolve, 5000))
    }
  }

  paymentError.value = t('paymentPendingError')
}

onMounted(async () => {
  if ((statusParam === 'success' || statusParam === 'error') && paymentAttemptIdParam) {
    await verifyReturnedPayment()
  } else {
    await fetchInvoice()
  }
  if (genericLanding.value) return
  if (statusParam === 'error' && !paymentAttemptIdParam) {
    paymentError.value = t('paymentFailedError')
    await nextTick()
    scrollToPayment()
  } else if (statusParam === 'success' && !paymentAttemptIdParam) {
    paymentError.value = t('paymentPendingError')
  }
  if (paymentError.value) {
    await nextTick()
    scrollToPayment()
  }
})

// Computed
const isOverdue = computed(() => {
  const today = new Date()
  return today > invoice.dueDate && remainingAmount.value > 0
})

const daysLeft = computed(() => {
  const today = new Date()
  const diffTime = invoice.dueDate.getTime() - today.getTime()
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
})

const remainingAmount = computed(() => Math.max(invoice.total - invoice.creditedAmount - invoice.paidAmount, 0))
const effectiveStatus = computed(() => {
  if (invoice.paidAmount + invoice.creditedAmount >= invoice.total) return 'paid'
  if (invoice.paidAmount > 0 || invoice.creditedAmount > 0) return 'partial'
  return invoice.status
})
const phoneHref = computed(() => 'tel:' + invoice.campground.phone.replace(/[^+0-9]/g, ''))

// Formatting helpers
const formatDate = (date: Date) => date.toLocaleDateString('sv-SE')
const formatCurrency = (amount: number) => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount)

// Status helpers
const getStatusClass = () => {
  if (isOverdue.value && remainingAmount.value > 0) return 'status-overdue'
  switch (effectiveStatus.value) {
    case 'paid': return 'status-paid'
    case 'partial': return 'status-partial'
    default: return 'status-pending'
  }
}
const getStatusText = () => {
  if (remainingAmount.value === 0) return t('paidStatus')
  if (isOverdue.value) return t('overdue')
  if (daysLeft.value <= 7) {
    const dayText = daysLeft.value === 1 ? t('daysLeft_one', { count: daysLeft.value }) : t('daysLeft_other', { count: daysLeft.value })
    return dayText
  }
  switch (effectiveStatus.value) {
    case 'partial': return t('partial')
    default: return t('unpaid')
  }
}

// Actions
const processPayment = async () => {
  if(routeNamespace === 'exempel') {
    alert(t('demoInvoiceAlert'))
    return
  }
  if (processing.value) return
  paymentError.value = null
  const amountToPay = (useCustomAmount.value && !customAmountError.value) ? customAmountNumber.value : remainingAmount.value
  if (amountToPay <= 0) return
  processing.value = true
  try {
    const success_url = new URL(window.location.href)
    success_url.searchParams.set('status', 'success')
    success_url.searchParams.delete('payment_attempt_id')
    const error_url = new URL(window.location.href)
    error_url.searchParams.set('status', 'error')
    error_url.searchParams.delete('payment_attempt_id')
    const url = `${getApiBase()}/payments/invoice/initiate`
    const payload = {
      invoice_id: rawInvoiceId,
      code: routeCode,
      payment_amount: Math.round(amountToPay * 100),
      success_url: success_url.toString(),
      error_url: error_url.toString()
    }
    const resp = await axios.post(url, payload, { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } })
    const paymentLink = resp.data?.data?.payment_link
    if (paymentLink) {
      window.location.href = paymentLink
    } else {
      throw new Error(t('initiatePaymentError'))
    }
  } catch (e: any) {
    paymentError.value = t('initiatePaymentError')
  } finally {
    processing.value = false
  }
}
const downloadInvoice = () => {
  if(routeNamespace === 'exempel') {
    alert(t('demoPdfAlert'))
    return
  }
  window.open(invoice.signedPdfLink, '_blank', 'noopener')
}

// Payment method logos
const cardDerived = ['visa', 'mastercard', 'applepay', 'googlepay']
const displayPaymentLogos = computed(() => {
  const base: string[] = []
  if (paymentMethods.value.includes('card')) base.push(...cardDerived)
  if (paymentMethods.value.includes('swish')) base.push('swish')
  // Exclude Klarna here; it has its own callout.
  return base
})
const getPaymentMethodLogo = (method: string) => {
  const logos: Record<string, string> = {
    visa: '/images/visa.svg',
    mastercard: '/images/mastercard.svg',
    swish: '/images/swish.svg',
    applepay: '/images/applepay.svg',
    googlepay: '/images/googlepay.svg',
    klarna: '/images/klarna.png'
  }
  return logos[method] || ''
}

// State additions for custom amount
const useCustomAmount = ref(false)
const customAmountInput = ref('')
const customAmountInputRef = ref<HTMLInputElement | null>(null)
const customAmountNumber = computed(() => {
  const n = parseFloat(customAmountInput.value.replace(',', '.'))
  if (isNaN(n)) return 0
  return Math.round(n * 100) / 100
})
const MIN_PART_PAYMENT = 100
const customAmountError = computed(() => {
  if (!useCustomAmount.value) return ''
  const val = customAmountNumber.value
  if (val <= 0) return t('enterAmount')
  if (val < MIN_PART_PAYMENT) return t('minPartPayment', { min: MIN_PART_PAYMENT })
  if (val > remainingAmount.value) return t('amountExceeds')
  return ''
})
const showCustomAmountOption = computed(() => remainingAmount.value > 1000 && daysLeft.value > 30 && !paymentMethods.value.includes('klarna'))
const activateCustomAmount = () => { 
  useCustomAmount.value = true; 
  customAmountInput.value = ''; 
  nextTick(() => { 
    customAmountInputRef.value?.focus(); 
  }); 
}
const cancelCustomAmount = () => { useCustomAmount.value = false; customAmountInput.value = '' }
const sanitizeCustomAmount = () => { customAmountInput.value = customAmountInput.value.replace(/[^0-9.,]/g, '') }
watch(customAmountNumber, (val) => {
  if (useCustomAmount.value && val === remainingAmount.value) {
    // Auto revert to full payment (deactivate custom amount)
    cancelCustomAmount()
  }
})
</script>
